import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const ContactPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    fullName: '',
    company: '',
    email: '',
    phone: '',
    departure: '',
    destination: '',
    date: '',
    passengers: '',
    notes: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);

  try {
    const res = await fetch('https://formspree.io/f/xvzvyajo', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    if (res.ok) {
      setIsSubmitted(true);
      toast.success('Your enquiry has been submitted successfully!');

      // ✅ 成功才 reset
      setTimeout(() => {
        setFormData({
          fullName: '',
          company: '',
          email: '',
          phone: '',
          departure: '',
          destination: '',
          date: '',
          passengers: '',
          notes: '',
        });
        setIsSubmitted(false);
      }, 3000);

    } else {
      toast.error('Failed to submit enquiry.');
    }

  } catch (err) {
    console.error(err);
    toast.error('Something went wrong.');
  }

  setIsSubmitting(false);
};
    
  const contactInfo = [
    {
      icon: <Phone className="w-5 h-5" />,
      label: 'Main Line',
      value: '+65 8188 8023',
      href: 'tel:+6581888023',
    },
    {
      icon: <Mail className="w-5 h-5" />,
      label: 'Charter Enquiries',
      value: 'charter@singaporeaircharter.com',
      href: 'mailto:charter@singaporeaircharter.com',
    },
  ];

  return (
    <div data-testid="contact-page" className="bg-white">
      <Toaster position="top-right" richColors />
      
      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center justify-start overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1569629743817-70d8db6c323b?w=1920&q=85"
            alt="Aircraft cockpit"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay"></div>
        </div>

        <div className="relative z-10 container-padding w-full pt-32 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block text-blue-700 text-sm font-medium tracking-widest uppercase mb-6">
              Contact Us
            </span>
            <h1 
              data-testid="contact-headline"
              className="text-4xl sm:text-5xl lg:text-6xl font-medium text-white leading-tight mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Let's Plan Your Journey
            </h1>
            <p className="text-lg text-white/80 font-light leading-relaxed max-w-2xl">
              24/7 availability for charter enquiries, quotes, and immediate assistance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-gap">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-1"
            >
              <span className="caption-text text-blue-700 mb-4 block">Get in Touch</span>
              <h2 
                className="text-2xl md:text-3xl font-medium text-slate-900 mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Contact Information
              </h2>
              <div className="gold-line mb-8"></div>

              <div className="space-y-6 mb-10">
                {contactInfo.map((item, index) => (
                  <a
                    key={index}
                    href={item.href}
                    data-testid={`contact-info-${index}`}
                    className="flex items-start gap-4 group"
                  >
                    <div className="text-blue-700 mt-1">{item.icon}</div>
                    <div>
                      <span className="text-xs text-slate-400 uppercase tracking-wider block mb-1">
                        {item.label}
                      </span>
                      <span className="text-slate-700 group-hover:text-blue-700 transition-colors">
                        {item.value}
                      </span>
                    </div>
                  </a>
                ))}
              </div>

              {/* Operating Hours */}
              <div className="flex items-start gap-4">
                <Clock className="w-5 h-5 text-blue-700 mt-1" />
                <div>
                  <span className="text-xs text-slate-400 uppercase tracking-wider block mb-1">
                    Operating Hours
                  </span>
                  <span className="text-slate-700">
                    24/7 Charter Support
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <div className="bg-slate-50 p-8 md:p-12">
                <h2 
                  className="text-2xl md:text-3xl font-medium text-slate-900 mb-2"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Charter Enquiry Form
                </h2>
                <p className="text-slate-600 mb-8">
                  Complete the form below and our team will respond as soon as possible.
                </p>

                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-16"
                  >
                    <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                    <h3 
                      className="text-2xl font-medium text-slate-900 mb-2"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      Enquiry Submitted
                    </h3>
                    <p className="text-slate-600">
                      Thank you for your enquiry. Our team will contact you shortly.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="contact-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="block text-xs text-slate-500 uppercase tracking-wider mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleChange}
                          required
                          data-testid="input-fullname"
                          className="input-minimal"
                          placeholder="John Smith"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 uppercase tracking-wider mb-2">
                          Company (Optional)
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          data-testid="input-company"
                          className="input-minimal"
                          placeholder="Company Name"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="block text-xs text-slate-500 uppercase tracking-wider mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          data-testid="input-email"
                          className="input-minimal"
                          placeholder="john@company.com"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 uppercase tracking-wider mb-2">
                          Phone *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          data-testid="input-phone"
                          className="input-minimal"
                          placeholder="+65 XXXX XXXX"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="block text-xs text-slate-500 uppercase tracking-wider mb-2">
                          Departure Location *
                        </label>
                        <input
                          type="text"
                          name="departure"
                          value={formData.departure}
                          onChange={handleChange}
                          required
                          data-testid="input-departure"
                          className="input-minimal"
                          placeholder="Singapore"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 uppercase tracking-wider mb-2">
                          Destination *
                        </label>
                        <input
                          type="text"
                          name="destination"
                          value={formData.destination}
                          onChange={handleChange}
                          required
                          data-testid="input-destination"
                          className="input-minimal"
                          placeholder="Hong Kong"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="block text-xs text-slate-500 uppercase tracking-wider mb-2">
                          Preferred Date *
                        </label>
                        <input
                          type="date"
                          name="date"
                          value={formData.date}
                          onChange={handleChange}
                          required
                          data-testid="input-date"
                          className="input-minimal"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 uppercase tracking-wider mb-2">
                          Number of Passengers *
                        </label>
                        <input
                          type="number"
                          name="passengers"
                          value={formData.passengers}
                          onChange={handleChange}
                          required
                          min="1"
                          data-testid="input-passengers"
                          className="input-minimal"
                          placeholder="4"
                        />
                      </div>
                    </div>

                    <div className="mb-8">
                      <label className="block text-xs text-slate-500 uppercase tracking-wider mb-2">
                        Additional Notes
                      </label>
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        rows={4}
                        data-testid="input-notes"
                        className="input-minimal resize-none"
                        placeholder="Any special requirements, preferences or return trip information..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="submit-enquiry-btn"
                      className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-red-600 text-white px-10 py-4 text-sm font-medium tracking-wider uppercase hover:bg-red-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit Enquiry
                          <Send className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
